import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Tabs, Tab, Card, Row, Col, Badge, Spinner, InputGroup } from 'react-bootstrap';
import { FaBolt, FaGasPump, FaTint, FaWifi, FaTv, FaPhone, FaSearch, FaReceipt, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';

const BillPaymentModal = ({ show, onHide, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('electricity');
  const [selectedProvider, setSelectedProvider] = useState('');
  const [customerNumber, setCustomerNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [customerInfo, setCustomerInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingBill, setFetchingBill] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [billDetails, setBillDetails] = useState(null);

  // Service providers data
  const providers = {
    electricity: [
      { id: 'delhi_bijli', name: 'Delhi Bijli Board', state: 'Delhi', logo: '/providers/dbb.png' },
      { id: 'mumbai_power', name: 'Mumbai Power Company', state: 'Maharashtra', logo: '/providers/mpc.png' },
      { id: 'tneb', name: 'Tamil Nadu Electricity Board', state: 'Tamil Nadu', logo: '/providers/tneb.png' },
      { id: 'kseb', name: 'Kerala State Electricity Board', state: 'Kerala', logo: '/providers/kseb.png' },
      { id: 'uppcl', name: 'UP Power Corporation Ltd', state: 'Uttar Pradesh', logo: '/providers/uppcl.png' },
      { id: 'bseb', name: 'Bihar State Electricity Board', state: 'Bihar', logo: '/providers/bseb.png' }
    ],
    gas: [
      { id: 'indraprastha_gas', name: 'Indraprastha Gas Limited', state: 'Delhi', logo: '/providers/igl.png' },
      { id: 'mahanagar_gas', name: 'Mahanagar Gas Limited', state: 'Maharashtra', logo: '/providers/mgl.png' },
      { id: 'sabarmati_gas', name: 'Sabarmati Gas Limited', state: 'Gujarat', logo: '/providers/sgl.png' },
      { id: 'gujarat_gas', name: 'Gujarat Gas Company', state: 'Gujarat', logo: '/providers/ggc.png' }
    ],
    water: [
      { id: 'delhi_jal', name: 'Delhi Jal Board', state: 'Delhi', logo: '/providers/djb.png' },
      { id: 'mumbai_water', name: 'Mumbai Water Supply', state: 'Maharashtra', logo: '/providers/mws.png' },
      { id: 'chennai_water', name: 'Chennai Water Board', state: 'Tamil Nadu', logo: '/providers/cwb.png' },
      { id: 'bangalore_water', name: 'Bangalore Water Supply', state: 'Karnataka', logo: '/providers/bws.png' }
    ],
    broadband: [
      { id: 'airtel_broadband', name: 'Airtel Broadband', type: 'Fiber', logo: '/providers/airtel.png' },
      { id: 'jio_fiber', name: 'Jio Fiber', type: 'Fiber', logo: '/providers/jio.png' },
      { id: 'bsnl_broadband', name: 'BSNL Broadband', type: 'ADSL/Fiber', logo: '/providers/bsnl.png' },
      { id: 'act_fibernet', name: 'ACT Fibernet', type: 'Fiber', logo: '/providers/act.png' },
      { id: 'tikona', name: 'Tikona Digital Networks', type: 'Wireless', logo: '/providers/tikona.png' }
    ],
    landline: [
      { id: 'bsnl_landline', name: 'BSNL Landline', type: 'Government', logo: '/providers/bsnl.png' },
      { id: 'airtel_landline', name: 'Airtel Landline', type: 'Private', logo: '/providers/airtel.png' },
      { id: 'tata_landline', name: 'Tata Landline', type: 'Private', logo: '/providers/tata.png' }
    ],
    cable: [
      { id: 'den_networks', name: 'DEN Networks', type: 'Cable TV', logo: '/providers/den.png' },
      { id: 'hathway', name: 'Hathway Cable', type: 'Cable TV', logo: '/providers/hathway.png' },
      { id: 'siti_cable', name: 'Siti Cable', type: 'Cable TV', logo: '/providers/siti.png' }
    ]
  };

  const billTypes = [
    { key: 'electricity', label: 'Electricity', icon: <FaBolt className="text-warning" /> },
    { key: 'gas', label: 'Gas', icon: <FaGasPump className="text-info" /> },
    { key: 'water', label: 'Water', icon: <FaTint className="text-primary" /> },
    { key: 'broadband', label: 'Broadband', icon: <FaWifi className="text-success" /> },
    { key: 'landline', label: 'Landline', icon: <FaPhone className="text-secondary" /> },
    { key: 'cable', label: 'Cable TV', icon: <FaTv className="text-danger" /> }
  ];

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedProvider('');
    setCustomerNumber('');
    setAmount('');
    setCustomerInfo(null);
    setBillDetails(null);
    setMessage({ type: '', text: '' });
  };

  const validateCustomerNumber = (number, type) => {
    switch (type) {
      case 'electricity':
      case 'water':
        return /^[A-Z0-9]{6,15}$/i.test(number);
      case 'gas':
        return /^[0-9]{10,12}$/.test(number);
      case 'broadband':
      case 'landline':
        return /^[A-Z0-9]{8,16}$/i.test(number);
      case 'cable':
        return /^[0-9]{8,12}$/.test(number);
      default:
        return number.length >= 6;
    }
  };

  const fetchBillDetails = async () => {
    if (!selectedProvider) {
      setMessage({ type: 'error', text: 'Please select a service provider' });
      return;
    }

    if (!validateCustomerNumber(customerNumber, activeTab)) {
      setMessage({ type: 'error', text: 'Please enter a valid customer number' });
      return;
    }

    setFetchingBill(true);
    setMessage({ type: '', text: '' });
    setBillDetails(null);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Mock bill details
      const mockBillDetails = {
        customerName: 'Customer Name',
        customerNumber: customerNumber,
        provider: providers[activeTab].find(p => p.id === selectedProvider)?.name,
        billAmount: Math.floor(Math.random() * (5000 - 500) + 500),
        dueDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toLocaleDateString(),
        billDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toLocaleDateString(),
        units: activeTab === 'electricity' ? Math.floor(Math.random() * 300 + 100) : null,
        lastReading: activeTab === 'electricity' ? Math.floor(Math.random() * 10000 + 5000) : null,
        currentReading: activeTab === 'electricity' ? Math.floor(Math.random() * 10000 + 8000) : null,
        connectionType: activeTab === 'electricity' ? 'Domestic' : 
                      activeTab === 'broadband' ? 'Unlimited Plan' : 'Regular',
        status: Math.random() > 0.3 ? 'Outstanding' : 'Overdue'
      };

      setBillDetails(mockBillDetails);
      setAmount(mockBillDetails.billAmount.toString());
      setCustomerInfo({
        name: mockBillDetails.customerName,
        number: customerNumber,
        provider: mockBillDetails.provider
      });

      setMessage({ 
        type: 'success', 
        text: `Bill fetched successfully. Amount due: ₹${mockBillDetails.billAmount}` 
      });

    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to fetch bill details. Please try again.' });
    } finally {
      setFetchingBill(false);
    }
  };

  const handlePayBill = async () => {
    if (!billDetails) {
      setMessage({ type: 'error', text: 'Please fetch bill details first' });
      return;
    }

    if (!amount || parseFloat(amount) < 1) {
      setMessage({ type: 'error', text: 'Please enter a valid amount' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 3000));

      const paymentData = {
        transactionId: 'BILL' + Date.now(),
        customerNumber: customerNumber,
        amount: parseFloat(amount),
        provider: billDetails.provider,
        billType: activeTab,
        status: 'Success',
        timestamp: new Date().toLocaleString()
      };

      setMessage({ 
        type: 'success', 
        text: `Bill payment successful! Transaction ID: ${paymentData.transactionId}` 
      });

      if (onSuccess) {
        onSuccess(paymentData);
      }

      // Auto-close after 3 seconds
      setTimeout(() => {
        onHide();
      }, 3000);

    } catch (error) {
      setMessage({ type: 'error', text: 'Payment failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedProvider('');
    setCustomerNumber('');
    setAmount('');
    setCustomerInfo(null);
    setBillDetails(null);
    setMessage({ type: '', text: '' });
  };

  const getTabIcon = (tab) => {
    const tabInfo = billTypes.find(t => t.key === tab);
    return tabInfo ? tabInfo.icon : <FaBolt />;
  };

  const getCustomerNumberLabel = (type) => {
    switch (type) {
      case 'electricity': return 'Consumer Number';
      case 'gas': return 'Customer ID';
      case 'water': return 'Connection Number';
      case 'broadband': return 'Account Number';
      case 'landline': return 'Phone Number';
      case 'cable': return 'Subscriber ID';
      default: return 'Customer Number';
    }
  };

  const getCustomerNumberPlaceholder = (type) => {
    switch (type) {
      case 'electricity': return 'Enter consumer number (6-15 chars)';
      case 'gas': return 'Enter 10-12 digit customer ID';
      case 'water': return 'Enter connection number';
      case 'broadband': return 'Enter account number';
      case 'landline': return 'Enter phone number';
      case 'cable': return 'Enter subscriber ID';
      default: return 'Enter customer number';
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          <FaReceipt className="me-2" />
          Bill Payment Services
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Service Type Tabs */}
        <Tabs activeKey={activeTab} onSelect={handleTabChange} className="mb-4">
          {billTypes.map((billType) => (
            <Tab 
              key={billType.key}
              eventKey={billType.key} 
              title={
                <span>
                  {getTabIcon(billType.key)}
                  <span className="ms-2">{billType.label}</span>
                </span>
              }
            >
              <div className="p-2">
                <h6 className="text-primary mb-3">{billType.label} Bill Payment</h6>
              </div>
            </Tab>
          ))}
        </Tabs>

        {/* Provider Selection */}
        {!selectedProvider && (
          <div className="mb-4">
            <h6 className="mb-3">Select Service Provider</h6>
            <Row>
              {providers[activeTab]?.map((provider) => (
                <Col md={4} sm={6} xs={12} key={provider.id} className="mb-3">
                  <Card 
                    className="h-100 provider-card cursor-pointer border-2"
                    onClick={() => setSelectedProvider(provider.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Card.Body className="text-center p-3">
                      <div className="provider-logo mb-2">
                        {getTabIcon(activeTab)}
                      </div>
                      <h6 className="mb-1">{provider.name}</h6>
                      <small className="text-muted">
                        {provider.state || provider.type}
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        )}

        {/* Customer Details Form */}
        {selectedProvider && (
          <Card className="mb-4">
            <Card.Header className="bg-light">
              <div className="d-flex justify-content-between align-items-center">
                <strong>
                  {providers[activeTab].find(p => p.id === selectedProvider)?.name}
                </strong>
                <Button variant="link" size="sm" onClick={handleReset}>
                  Change Provider
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>{getCustomerNumberLabel(activeTab)} *</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        placeholder={getCustomerNumberPlaceholder(activeTab)}
                        value={customerNumber}
                        onChange={(e) => setCustomerNumber(e.target.value.toUpperCase())}
                      />
                      <Button 
                        variant="outline-primary" 
                        onClick={fetchBillDetails}
                        disabled={!customerNumber || fetchingBill}
                      >
                        {fetchingBill ? <Spinner size="sm" /> : <FaSearch />}
                      </Button>
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Amount (₹)</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="Bill amount"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      readOnly={!!billDetails}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* Bill Details */}
        {billDetails && (
          <Card className="mb-4">
            <Card.Header className="bg-success text-white">
              <FaCheckCircle className="me-2" />
              Bill Details
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="mb-2"><strong>Customer Name:</strong> {billDetails.customerName}</div>
                  <div className="mb-2"><strong>Customer Number:</strong> {billDetails.customerNumber}</div>
                  <div className="mb-2"><strong>Connection Type:</strong> {billDetails.connectionType}</div>
                  {billDetails.units && (
                    <div className="mb-2"><strong>Units Consumed:</strong> {billDetails.units} kWh</div>
                  )}
                </Col>
                <Col md={6}>
                  <div className="mb-2"><strong>Bill Date:</strong> {billDetails.billDate}</div>
                  <div className="mb-2"><strong>Due Date:</strong> {billDetails.dueDate}</div>
                  <div className="mb-2"><strong>Amount Due:</strong> ₹{billDetails.billAmount}</div>
                  <div className="mb-2">
                    <strong>Status:</strong> 
                    <Badge bg={billDetails.status === 'Outstanding' ? 'warning' : 'danger'} className="ms-2">
                      {billDetails.status}
                    </Badge>
                  </div>
                </Col>
              </Row>
              {billDetails.status === 'Overdue' && (
                <Alert variant="warning" className="mt-3 mb-0">
                  <FaExclamationTriangle className="me-2" />
                  This bill is overdue. Please pay immediately to avoid service disconnection.
                </Alert>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Message */}
        {message.text && (
          <Alert variant={message.type === 'error' ? 'danger' : 'success'} className="mb-3">
            {message.text}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div>
            {selectedProvider && (
              <Button variant="outline-secondary" onClick={handleReset}>
                Reset
              </Button>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={onHide} className="me-2">
              Cancel
            </Button>
            <Button 
              variant="success" 
              onClick={handlePayBill}
              disabled={loading || !billDetails || !amount}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Processing...
                </>
              ) : (
                <>
                  <FaReceipt className="me-2" />
                  Pay ₹{amount || '0'}
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default BillPaymentModal;
